import { baseApi } from '@/store/baseApi';
import { CacheDurations } from '@web/utils';

import type {
  CurrentUserContextResponse,
  PatchUserPayload,
  UserProfile,
} from '@/features/users/types';
import type { APIResponse } from '@web/types';

export const usersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCurrentUserContext: builder.query<
      APIResponse<CurrentUserContextResponse>,
      void
    >({
      query: () => ({
        url: 'auth/me',
        method: 'GET',
      }),
      keepUnusedDataFor: CacheDurations.SHORT,
      providesTags: [{ type: 'CurrentUserContext', id: 'CURRENT' }],
    }),
    getUserById: builder.query<APIResponse<UserProfile>, string>({
      query: (userId) => ({
        url: `users/${userId}`,
        method: 'GET',
      }),
      keepUnusedDataFor: CacheDurations.SHORT,
    }),
    patchUserById: builder.mutation<
      APIResponse<UserProfile>,
      { userId: string; payload: PatchUserPayload }
    >({
      query: ({ userId, payload }) => ({
        url: `users/${userId}`,
        method: 'PATCH',
        body: payload,
      }),
      async onQueryStarted({ userId }, { dispatch, queryFulfilled }) {
        try {
          const { data: response } = await queryFulfilled;
          if (!response.success || !response.data) {
            return;
          }

          const nextUser = response.data;

          dispatch(
            usersApi.util.updateQueryData('getUserById', userId, (draft) => {
              if (!draft) {
                return;
              }

              draft.success = true;
              draft.data = {
                ...(draft.data || ({} as UserProfile)),
                ...nextUser,
              };
            }),
          );

          dispatch(
            usersApi.util.updateQueryData(
              'getCurrentUserContext',
              undefined,
              (draft) => {
                if (!draft?.data) {
                  return;
                }

                draft.success = true;
                draft.data.user = {
                  ...(draft.data.user || ({} as UserProfile)),
                  ...nextUser,
                };
              },
            ),
          );
        } catch {
          // Ignore optimistic cache sync failures; request error handling stays in component layer.
        }
      },
    }),
  }),
});

export const {
  useGetCurrentUserContextQuery,
  useGetUserByIdQuery,
  usePatchUserByIdMutation,
} = usersApi;
